import { useCallback, useMemo } from 'react';
import Head from 'next/head';
import { useQueryClient } from '@tanstack/react-query';
import { ObjectBrowser } from '@/components/ObjectBrowser';
import type { BrowserFetcherResult } from '@/components/ObjectBrowser';
import { EntityCard } from '@/components/EntityCard';
import { entitiesApi } from '@/lib/api';
import type { Entity } from '@/lib/api/types';
import type { MapBrowserObject } from '@/components/ObjectMapBrowser';
import { useSessionState } from '@/components/SessionState';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { useEntityDetailSheet, buildEntityNavigationList } from '@/lib/hooks/useEntityDetailSheet';
import { useCountries } from '@/lib/hooks/useCountries';
import { ENTITY_ROLE_OPTIONS } from '@sizzlenz/shared';
import { getDirectoryFilterConfigs, DIRECTORY_URL_SYNC_FILTERS, buildDirectoryInitialFilters, syncDirectoryFilters, buildDirectoryApiQuery } from '@/lib/directory-filters';
import { useSiteSettings } from '@/lib/hooks/useSiteSettings';
import { DirectoryQuickFilters } from '@/components/directory/DirectoryQuickFilters';
import { DirectoryListingCallout } from '@/components/directory/DirectoryListingCallout';
import { DirectoryListingButton } from '@/components/directory/DirectoryListingButton';
function entityToMapObject(entity: Entity): MapBrowserObject {
  const addressData = (entity.data as Record<string, any>)?.address_1;
  const lat = addressData?.geometry?.location?.lat;
  const lng = addressData?.geometry?.location?.lng;
  const roles = (entity.data as any)?.roles;
  const roleLabels = Array.isArray(roles) ? roles.map((roleId: string) => ENTITY_ROLE_OPTIONS.find(r => r.id === roleId)?.label).filter(Boolean) as string[] : [];
  return {
    id: entity.id,
    objectType: 'entity',
    lat: lat ?? undefined,
    lng: lng ?? undefined,
    title: entity.name_1 ?? undefined,
    image: entity.image ?? undefined,
    href: `/entities/${entity.id}`,
    tags: roleLabels,
    data: entity
  };
}
export default function ProviderDirectoryPage() {
  const queryClient = useQueryClient();
  const {
    sessionHash
  } = useSessionState();
  const {
    showEntityDetail
  } = useEntityDetailSheet();
  const {
    data: countries = []
  } = useCountries();
  const {
    settings,
    isLoading: siteSettingsIsLoading
  } = useSiteSettings();
  const countryOptions = useMemo(() => countries.sort((a, b) => a.name.localeCompare(b.name)).map(c => ({
    id: c.alpha3,
    label: `${c.emoji} ${c.name}`
  })), [countries]);
  const filterConfigs = useMemo(() => getDirectoryFilterConfigs(countryOptions.length > 0 ? countryOptions : undefined), [countryOptions]);
  const {
    values,
    handlers,
    isReady
  } = useUrlSync({
    filters: DIRECTORY_URL_SYNC_FILTERS
  });
  const initialFilters = useMemo(() => buildDirectoryInitialFilters(values), [values.role.join(','), values.capability.join(','), values.specialisation.join(','), values.channelExpertise.join(','), values.geoPresence.join(','), values.geoExpertise.join(',')]);
  const fetcher = useCallback(async (params: {
    search?: string;
    filters: Record<string, string[]>;
    cursor?: string;
    limit: number;
  }): Promise<BrowserFetcherResult<Entity>> => {
    const query = buildDirectoryApiQuery({
      ...params,
      sessionHash
    });
    const response = await queryClient.fetchQuery({
      queryKey: ['entities', query],
      queryFn: () => entitiesApi.getEntities(query),
      staleTime: 1000 * 10
    });
    return {
      objects: response.entities,
      hasNextPage: !!response.pagination.nextCursor,
      nextCursor: response.pagination.nextCursor ?? undefined
    };
  }, [sessionHash, queryClient]);
  const handleSearchChange = useCallback((search: string) => handlers.search(search), [handlers.search]);
  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => syncDirectoryFilters(filters, handlers), [handlers.role, handlers.capability, handlers.specialisation, handlers.channelExpertise, handlers.geoPresence, handlers.geoExpertise]);
  const handleShowDetail = useCallback((obj: MapBrowserObject, allObjects?: MapBrowserObject[]) => {
    if (obj.objectType === 'entity') {
      const entity = obj.data as Entity;
      const entityObjects = (allObjects || []).filter(o => o.objectType === 'entity');
      const navigationList = entityObjects.length > 1 ? buildEntityNavigationList(entityObjects.map(o_0 => o_0.data as Entity), entity.type || 'brand') : undefined;
      showEntityDetail(entity.id, entity.type || 'brand', {
        title: entity.name_1,
        navigationList
      });
    }
  }, [showEntityDetail]);
  if (!isReady || siteSettingsIsLoading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>;
  }
  return <>
      <Head>
        <title>Directory | Sizzlenz</title>
      </Head>
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Directory</h1>
            <DirectoryListingButton />
          </div>
          <DirectoryListingCallout variant="compact" dismissible className="mb-4" />
          <DirectoryQuickFilters className="mb-3" value={initialFilters} onChange={handleFiltersChange} />
          <ObjectBrowser<Entity> fetcher={fetcher} renderCard={entity_0 => <EntityCard entity={entity_0} entityType={entity_0.type} variant="compact" showRoles />} getKey={entity_1 => entity_1.id} filters={filterConfigs} searchEnabled searchPlaceholder="Search..." limit={40} emptyState={{
          title: 'No matches',
          description: 'Try adjusting your search or role filters'
        }} initialSearch={values.search} initialFilters={initialFilters} onSearchChange={handleSearchChange} onFiltersChange={handleFiltersChange} toMapObject={entityToMapObject} mapConfig={{
          enabled: false,
          defaultShowMap: true,
          onShowDetail: handleShowDetail,
          initialCenter: settings?.directory?.initialMapCenter,
          initialZoom: settings?.directory?.initialMapZoom
        }} />
        </div>
      </div>
    </>;
}